import { VisualisationContextProps } from 'widgets/supportVisualisation/Content/VisualisationContentProps';
import React, { useReducer, useState } from 'react';
import './StreamTest.scss';
import { TextInput } from '@instinet-analytics/semantic-ui-components';
import { Dropdown } from '@instinet-analytics/semantic-ui-components';
import { TextAreaInput } from '@instinet-analytics/semantic-ui-components';
import { ChannelDestination } from 'communication/Channel';
import { Button } from '@instinet-analytics/semantic-ui-components';
import { useServices } from 'app/hooks';
import { useSelector } from 'react-redux';
import { selectChannelId } from 'streamsState/store/streamStatuses/selectors';
import { StreamError } from 'streamsState/store/streamStatuses/StreamErrorState';
import State from 'store/State';
import { Grid } from '@instinet-analytics/semantic-ui-components';
import { ColDef } from 'ag-grid-community/dist/lib/entities/colDef';
import { toDateString } from 'widgets/supportVisualisation/SupportVisualisationHelpers';
import { GetRowClassParams } from '@instinet-analytics/semantic-ui-components';
import JsonPanel from 'utils/components/JsonPanel/JsonPanel';
import { getTemplateRequest } from 'widgets/supportVisualisation/Content/StreamTest/templateRequests';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyMessage = any | StreamError;

interface HandledMessage {
    id: number;
    type: 'Request' | 'Response';
    status: 'OK' | 'Error';
    time: Date;
    data: AnyMessage;
}

type MessageAction =
    | { type: 'clear' }
    | { type: 'message'; data: AnyMessage }
    | { type: 'error'; error: StreamError }
    | { type: 'request'; data: AnyMessage };
type MessagesState = {
    id: number;
    messages: HandledMessage[];
};

function messagesReducer(
    state: MessagesState,
    action: MessageAction
): MessagesState {
    const id = state.id + 1;

    if (action.type === 'clear') {
        return {
            ...state,
            messages: [],
        };
    }
    return {
        id,
        messages: [
            ...state.messages,
            {
                id,
                status: action.type === 'error' ? 'Error' : 'OK',
                type: action.type === 'request' ? 'Request' : 'Response',
                time: new Date(),
                data: action.type === 'error' ? action.error : action.data,
            },
        ],
    };
}
export default function StreamTest(props: VisualisationContextProps) {
    const { height, width } = props;

    const { streamDataService } = useServices();

    const [channelInputValue, setChannelInputValue] = useState('');
    const [channelDropdownValue, setChannelDropdownValue] = useState('');
    const [requestInputValue, setRequestInputValue] = useState('');
    const [streamKey, setStreamKey] = useState<string | undefined>(undefined);
    const [{ messages }, dispatchMessage] = useReducer(messagesReducer, {
        id: 0,
        messages: [],
    });
    const [selectedMessage, setSelectedMessage] = useState<
        HandledMessage | undefined
    >(undefined);

    const channelId = useSelector((s: State) => selectChannelId(s, streamKey));

    const onChannelInputChanged = (nextValue: string) =>
        setChannelInputValue(nextValue);

    const onChannelDropdownChange = (nextValue: string) => {
        setChannelDropdownValue(nextValue);
        if (nextValue !== '') {
            setChannelInputValue(nextValue);
        }

        const templateRequest = getTemplateRequest(
            nextValue as ChannelDestination
        );
        if (templateRequest) {
            setRequestInputValue(JSON.stringify(templateRequest));
        }
    };

    const columnDefs = getColumns(width);

    const onMessage = (streamKey: string, data: AnyMessage) => {
        dispatchMessage({ type: 'message', data });
    };

    const onError = (streamKey: string, error: StreamError) => {
        dispatchMessage({ type: 'error', error });
    };

    const onPending = (nextStreamKey: string) => {
        setStreamKey(nextStreamKey);
    };

    function clearGrid() {
        dispatchMessage({ type: 'clear' });
    }

    const onClickGo = () => {
        if (!streamKey) {
            clearGrid();
        }
        const data = JSON.parse(requestInputValue);

        (async () => {
            await streamDataService.startAndWatchStream(
                `StreamTest-${new Date().getTime()}`,
                channelInputValue as ChannelDestination,
                {
                    onMessage,
                    onError,
                    onPending,
                },
                channelId
            )(data);
        })();
        dispatchMessage({ type: 'request', data });
    };

    const onClickStop = () => {
        if (streamKey) {
            streamDataService.stopStream(streamKey);
            setStreamKey(undefined);
        }
    };

    const onSelectedRowIdsChanged = (rowDataIds: string[]) => {
        if (rowDataIds.length !== 1) {
            setSelectedMessage(undefined);
        }
        const row = messages.find((r) => r.id.toString() === rowDataIds[0]);
        setSelectedMessage(row);
    };

    const style = { height, width };
    const topStyle = { height: 200, width };
    const bottomStyle = { height: height - 200, width };
    const bottomSectionStyle = { height: (height - 200) / 2, width };

    return (
        <div className="StreamTest" style={style}>
            <div style={topStyle}>
                <div className="channel-selection">
                    <div className="title">Channel:</div>
                    <TextInput
                        className="channel-input"
                        value={channelInputValue}
                        onChange={onChannelInputChanged}
                        placeholder={'Type or select a channel'}
                    />
                    <ChannelDropdown />
                </div>
                <div className="request">
                    <div className="title">Request:</div>
                    <TextAreaInput
                        value={requestInputValue}
                        onChange={(v) => setRequestInputValue(v)}
                    />
                    <Button
                        className="primary"
                        onClick={onClickGo}
                        disabled={channelInputValue === ''}
                    >
                        {streamKey ? 'Update' : 'Send'}
                    </Button>
                    {streamKey && (
                        <Button className="primary" onClick={onClickStop}>
                            Stop
                        </Button>
                    )}
                </div>
                {streamKey && (
                    <div className="stream-info">
                        <div className="title">Stream Key:</div>
                        <div className="value">{streamKey}</div>
                        <div className="title">Channel:</div>
                        <div className="value">{channelId}</div>
                        <div className="clear-grid" onClick={clearGrid}>
                            Clear Grid
                        </div>
                    </div>
                )}
            </div>

            <div className="messages" style={bottomStyle}>
                <Grid
                    autoSizeColumns={true}
                    singleSelectOnly={true}
                    columnDefs={columnDefs}
                    rowData={messages}
                    rowClassRules={getRowClassRules()}
                    getRowDataId={(r) => r.id.toString()}
                    onSelectedRowIdsChanged={onSelectedRowIdsChanged}
                />
                {selectedMessage && (
                    <div
                        className="selected-message"
                        style={bottomSectionStyle}
                    >
                        <div className="message-info" style={{ height: 40 }}>
                            <div>{toDateString(selectedMessage.time)}</div>
                            <div>{selectedMessage.status}</div>
                            <div>{selectedMessage.type}</div>
                        </div>
                        <div
                            className="json"
                            style={{ height: bottomSectionStyle.height - 40 }}
                        >
                            <JsonPanel data={selectedMessage.data} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    function ChannelDropdown() {
        const options = [
            'startOrderColumnsStream',
            'startExpertiseByOrderIdsStream',
            'startFillsGraphByOrderIdOrSymQtyStream',
            'startIntradayProfilesStream',
            'startLiquidityStream',
            'startPLGraphStream',
            'startPortfolioReturnsStream',
            'startParmUpdates',
            'startPfeStream',
            'startPriceChartStream',
            'startRsiStream',
            'startStrategyParamsCurByOrderIdsStream',
            'startStrategyParamsHistByOrderIdsStream',
            'startVenueAnalysisByOrderIdsStream',
            'startVenueAnalysisByOrderIdsOrSymbolStream',
            'startOpenSubmissionsByOrderIdsStream',
            'symbolInfo',
            'startSymbolTilesStream',
            'INIT',
            'resend',
            'renew',
        ].map((v) => ({ text: v, value: v }));
        return (
            <Dropdown
                options={options}
                className="ChannelDropdown"
                value={channelDropdownValue}
                onChange={onChannelDropdownChange}
            />
        );
    }
}

function getColumns(width: number): ColDef[] {
    const timeWidth = 80;
    const statusAndTypeWidth = 60;
    const payloadWidth =
        width - timeWidth - statusAndTypeWidth - statusAndTypeWidth - 20;
    return [
        {
            field: 'time',
            headerName: 'Time',
            resizable: true,
            width: timeWidth,
            cellRenderer: (props: { value: Date }) => {
                return <span>{toDateString(props.value)}</span>;
            },
        },

        { field: 'status', headerName: 'Status', width: statusAndTypeWidth },
        { field: 'type', headerName: 'Type', width: statusAndTypeWidth },

        {
            field: 'payload',
            headerName: 'Payload',
            resizable: true,
            width: payloadWidth,
            cellRenderer: (props: { data: string | Date }) => {
                return `<span>
                     ${
                         typeof props.data === 'string'
                             ? props.data
                             : JSON.stringify(props.data)
                     }
                    </span>`;
            },
        },
    ];
}

function getRowClassRules() {
    return {
        requestRow: (params: GetRowClassParams<HandledMessage>) => {
            const item: HandledMessage = params.data;
            return item.type === 'Request';
        },
        responseRow: (params: GetRowClassParams<HandledMessage>) => {
            const item: HandledMessage = params.data;
            return item.type === 'Response';
        },
        errorRow: (params: GetRowClassParams<HandledMessage>) => {
            const item: HandledMessage = params.data;
            return item.status === 'Error';
        },
    };
}
